// 6.5 de-mock: the single source that feeds the shell's @perlab/auth-bridge provider.
// Fetches /auth/session once on mount, maps the backend identity+roles+tenant to the bridge's
// AuthUser, and exposes a hydration gate so the host renders NOTHING (never a default/admin user)
// until the real session resolves. Fail-closed: any error / no session → user=null (signed-out).
// Mirrors the real lib/auth AuthContext hydration pattern (AuthContext.tsx) — one identity truth.

import { useEffect, useState } from "react";
import type { AuthUser } from "@perlab/auth-bridge";
import { fetchSession, type SessionIdentity } from "./api";

function toAuthUser(s: SessionIdentity): AuthUser {
  return {
    userId: s.sub,
    displayName: s.name || s.email || s.sub,
    email: s.email ?? "",
    tenant: s.tenant ?? "",
    roles: s.roles ?? [], // defend a contract-drift partial body (post-build red-team LOW)
  };
}

export interface SessionIdentityState {
  /** null = no resolved session (signed-out). Never a default. */
  user: AuthUser | null;
  /** Raw admission state used by the request-access surface. */
  session: SessionIdentity | null;
  /** false until /auth/session resolves — the host must not render the shell before this is true. */
  hydrated: boolean;
}

export function useSessionIdentity(): SessionIdentityState {
  const [state, setState] = useState<SessionIdentityState>({ user: null, session: null, hydrated: false });

  useEffect(() => {
    let alive = true;
    void fetchSession().then((s) => {
      if (!alive) return;
      setState({ user: s ? toAuthUser(s) : null, session: s, hydrated: true });
    });
    return () => {
      alive = false;
    };
  }, []);

  return state;
}
